import Button from '@components/Button';
import Input from '@components/Input';
import { sendSuccess } from '@components/Noti';
import TextLink from '@components/TextLink';
import Tip from '@components/Tip';
import TourQr from '@components/TourQr';
import IconHelp from '@components/icons/IconHelp';
import { API_DOMAIN, HOST, VIP_URL } from '@constants';
import { PrivilegeType } from '@types';
import { getAppKey } from '@utils/getAppKey';
import getCookie from '@utils/getCookie';
import getTid from '@utils/getTid';
import { teamConfig } from '@utils/teamConfig';
import { getMediaUrl } from '@utils/urlMakers';
import axios from 'axios';
import { customAlphabet } from 'nanoid';
import { useMutation } from 'react-query';
import { selectLibImages } from '../../mediaLib';
import { ShareBasicSeatsSetting } from './ShareBasicSeatsSetting';
import IconVip from './img-vip.png';
import type { OnShareSettingChange, ShareSetting } from './types';

const OPTION_NAME = 'access_type';

type Props = {
  settings: ShareSetting;
  isVip: boolean;
  isTeamAccount: boolean;
  onChange: OnShareSettingChange;
};

const initPwd = customAlphabet('0123456789abcdefghijklmnopqrstuvwxyz', 4);

export function ShareBasic({
  settings,
  isVip,
  isTeamAccount,
  onChange,
}: Props) {
  const tourUrl = `${HOST}/vr/${getTid()}`;
  const iFrameStr = `<iframe src="${tourUrl}" frameborder="no" width="700" height="500" ></iframe>`;
  const { privilege, password, shareConfig } = settings;
  const { qrMediaUrl } = shareConfig;

  const shareExport = useMutation(['share-export'], () => {
    const tid = getTid();
    const url = teamConfig(
      `${API_DOMAIN}/member/my/tour/${tid}/export`,
      `${API_DOMAIN}/team/app/tours/${tid}/export`
    );
    return axios
      .post(url, undefined, {
        responseType: 'blob',
        headers: {
          'App-Key': getAppKey(),
          'App-Authorization': getCookie(),
        },
      })
      .then((response) => {
        const blob = new Blob([response.data], {
          type: 'application/octet-stream',
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;

        // 从响应头中获取文件名
        const contentDisposition = response.headers['content-disposition'];
        let fileName = 'download.xlsx'; // 默认文件名
        if (contentDisposition) {
          const matches = contentDisposition.match(/filename="(.+)"/);
          if (matches.length > 1) {
            fileName = decodeURIComponent(matches[1]);
          }
        }

        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      });
  });

  const selectQrImage = async () => {
    const { libItems } = await selectLibImages();
    if (!libItems) return;
    onChange((draft) => {
      draft.shareConfig.qrMediaUrl = libItems[0].url;
      draft.shareConfig.qrMediaId = libItems[0].id;
    });
  };

  const renderHeader = () => {
    return (
      <div className="flex flex-col bg-white-light p-20">
        <div
          className="flex items-center"
          onChange={(e) => {
            const target = e.target as HTMLInputElement;
            if (target.name === OPTION_NAME) {
              const targetVal = Number.parseInt(target.value) as PrivilegeType;
              onChange((draft) => {
                draft.privilege = targetVal;
                if (targetVal === PrivilegeType.password && !draft.password) {
                  draft.password = initPwd();
                }
              });
            }
          }}
        >
          <div>访问设置：</div>
          <label className="ml-20 flex cursor-pointer items-center">
            <input
              type="radio"
              readOnly
              value={PrivilegeType.public}
              checked={privilege === PrivilegeType.public}
              name={OPTION_NAME}
            />
            <span className="ml-5">允许访问</span>
          </label>
          <label className="ml-20 flex cursor-pointer items-center">
            <input
              type="radio"
              readOnly
              value={PrivilegeType.private}
              checked={privilege === PrivilegeType.private}
              name={OPTION_NAME}
            />
            <span className="ml-5">不允许访问</span>
          </label>
          <label className="ml-20 flex cursor-pointer items-center">
            <input
              type="radio"
              readOnly
              value={PrivilegeType.password}
              checked={privilege === PrivilegeType.password}
              name={OPTION_NAME}
            />
            <span className="ml-5">密码访问</span>
            <img
              src={IconVip}
              alt=""
              className="ml-5 h-16 w-16 flex-shrink-0"
            />
          </label>
          <label className="ml-20 flex cursor-pointer items-center">
            <input
              type="radio"
              readOnly
              value={PrivilegeType.team}
              checked={privilege === PrivilegeType.team}
              name={OPTION_NAME}
            />
            <span className="ml-5">企业内部访问</span>
            <img
              src={IconVip}
              alt=""
              className="ml-5 h-16 w-16 flex-shrink-0"
            />
          </label>
        </div>
        <div className="mt-20 flex items-center">
          {privilege === PrivilegeType.public && (
            <div className="text-orange">
              允许所有人可以通过地址正常访问作品。
            </div>
          )}
          {privilege === PrivilegeType.private && (
            <div className="text-orange">
              作品地址不可访问，仅在管理中心可以编辑预览。
            </div>
          )}
          {privilege === PrivilegeType.password && isVip && (
            <>
              <div>访问密码：</div>
              <Input
                className="ml-20 h-30 grow"
                value={password}
                maxLength={20}
                onChange={(e) => {
                  onChange((draft) => {
                    draft.password = e.target.value;
                  });
                }}
              />
            </>
          )}
          {privilege === PrivilegeType.password && !isVip && (
            <>
              <div>该功能为个人专业版/企业版会员功能，请升级后再使用</div>
              <TextLink href={VIP_URL} target="_blank" className="ml-20">
                去升级
              </TextLink>
            </>
          )}
          {privilege === PrivilegeType.team && isTeamAccount && (
            <ShareBasicSeatsSetting />
          )}
          {privilege === PrivilegeType.team && !isTeamAccount && (
            <>
              <div>该功能为企业版会员功能，请升级后再使用</div>
              <TextLink
                href={`${HOST}/vip/720vrqiye`}
                target="_blank"
                className="ml-20"
              >
                去升级
              </TextLink>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderBody = () => {
    return (
      <div className="flex p-20">
        <div className="grow">
          <div className="flex">
            <div>作品地址：</div>
            <TextLink
              className="ml-auto"
              target="_blank"
              onClick={() => shareExport.mutateAsync()}
            >
              导出所有场景分享链接
            </TextLink>
            <TextLink className="ml-15" href={tourUrl} target="_blank">
              新窗口打开
            </TextLink>
          </div>
          <div className="mt-5 flex items-center">
            <Input value={tourUrl} disabled className="h-30 grow" />
            <Button
              className="ml-10"
              onClick={() => {
                const text =
                  settings.privilege === PrivilegeType.password
                    ? `${tourUrl}\n访问密码: ${settings.password ?? ''}`
                    : tourUrl;
                navigator.clipboard?.writeText(text).then(() => {
                  sendSuccess('作品地址已复制到剪贴板');
                });
              }}
            >
              复制
            </Button>
          </div>
          <div className="mt-20 flex items-center">
            <div>嵌入到网站：</div>
            <a
              href="https://bbs2.720yun.com/article?id=378"
              target="_blank"
              rel="noreferrer"
            >
              <IconHelp scheme="light" />
            </a>
          </div>
          <div className="mt-5 flex items-center">
            <Input value={iFrameStr} disabled className="h-30 grow" />
            <Button
              className="ml-10"
              onClick={() => {
                navigator.clipboard?.writeText(iFrameStr).then(() => {
                  sendSuccess('嵌入地址已复制到剪贴板');
                });
              }}
            >
              复制
            </Button>
          </div>
          <Tip className="mt-20">
            如嵌入后显示异常，请在html页面的head里加入以下代码：
          </Tip>
          <Tip>
            {`<meta name="viewport" content="width=device-width, user-scalable=no, initial-scale=1.0, maximum-scale=1.0, minimum-scale=1.0" />`}
          </Tip>
        </div>
        <div className="ml-20 shrink-0">
          <div>作品二维码</div>
          <TourQr
            className="mt-5 h-130 w-130"
            imageUrl={qrMediaUrl ? getMediaUrl(qrMediaUrl) : undefined}
          />
          <Button className="mt-10 w-full" onClick={selectQrImage}>
            修改图标
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div>
      {renderHeader()}
      {renderBody()}
    </div>
  );
}
